import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import LoadingSpinner from './components/LoadingSpinner';
import BottomNav from './components/BottomNav';
import DailyTipPopup from './components/DailyTipPopup';

// ChunkErrorBoundary - handles chunk loading errors with auto-recovery
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a chunk loading error
    if (error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
      return { hasError: true };
    }
    throw error; // Re-throw if not a chunk error
  }

  componentDidCatch(error, errorInfo) {
    if (error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
      const { errorCount } = this.state;
      
      // Auto-reload only once to prevent infinite loops
      if (errorCount === 0) {
        console.log('ChunkLoadError detected, reloading page...');
        this.setState({ errorCount: errorCount + 1 });
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError && this.state.errorCount > 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Update Required</h2>
            <p className="text-gray-600 mb-6">
              A new version is available. Please refresh the page to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gradient-to-r from-rose-500 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Eager load critical components (no loading delay)
import HomePage from './components/HomePage';
import AuthPage from './components/AuthPage';
import AboutPage from './components/AboutPage';
import FAQPage from './components/FAQPage';
import ContactPage from './components/ContactPage';
import PremiumPage from './components/PremiumPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';

// Lazy load non-critical components (load on demand)
const QuizPage = lazy(() => import('./components/QuizPage'));
const ResultsPage = lazy(() => import('./components/ResultsPage'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const Admin2Login = lazy(() => import('./components/Admin2Login'));
const Admin2Dashboard = lazy(() => import('./components/Admin2Dashboard'));
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const EmailVerification = lazy(() => import('./components/EmailVerification'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const PasswordReset = lazy(() => import('./components/PasswordReset'));
const CouplesMode = lazy(() => import('./components/CouplesMode'));
const CouplesInviteAccept = lazy(() => import('./components/CouplesInviteAccept'));
const StorePage = lazy(() => import('./components/StorePage'));
const ProductDetailPage = lazy(() => import('./components/ProductDetailPage'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const PersonalizedTests = lazy(() => import('./components/PersonalizedTests'));
const CommissionApplication = lazy(() => import('./components/CommissionApplication'));
const CommissionLogin = lazy(() => import('./components/CommissionLogin'));
const CommissionDashboard = lazy(() => import('./components/CommissionDashboard'));
const CommissionTerms = lazy(() => import('./components/CommissionTerms'));
const MessagesPage = lazy(() => import('./components/MessagesPage'));
const UserProfilePage = lazy(() => import('./components/UserProfilePage'));
const GamesPage = lazy(() => import('./components/GamesPage'));
const CoursesPage = lazy(() => import('./components/CoursesPage'));
const QuestionBoard = lazy(() => import('./components/QuestionBoard'));
const CouplesCompatibility = lazy(() => import('./components/CouplesCompatibility'));
const ChallengesPage = lazy(() => import('./components/ChallengesPage'));
const MembersOnlyPage = lazy(() => import('./components/MembersOnlyPage'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./components/PaymentCancel'));
const EmotionsPage = lazy(() => import('./components/EmotionsPage'));
const SharedResult = lazy(() => import('./components/SharedResult'));
const InviteLanding = lazy(() => import('./components/InviteLanding'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));
const Leaderboard = lazy(() => import('./components/Leaderboard'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [showDailyTip, setShowDailyTip] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      console.log('AuthContext: Starting login for:', email);
      console.log('AuthContext: API URL:', `${API}/auth/login`);
      
      const response = await axios.post(`${API}/auth/login`, { email, password });
      console.log('AuthContext: Login response:', response.data);
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      // Show daily tip popup only once per session
      const dailyTipShown = sessionStorage.getItem('dailyTipShown');
      if (!dailyTipShown) {
        setShowDailyTip(true);
        sessionStorage.setItem('dailyTipShown', 'true');
      }
      
      console.log('AuthContext: Login successful, user set:', userData);
      return { success: true, user: userData };
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      console.error('AuthContext: Error response:', error.response?.data);
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (email, password, fullName) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name: fullName
      });
      
      // Registration successful - email verification required
      // Do NOT auto-login, user must verify email first
      return { 
        success: true, 
        message: response.data.message,
        verification_required: response.data.verification_required 
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Daily Tip Popup - Shows once per login session */}
      {showDailyTip && user && (
        <DailyTipPopup 
          onClose={() => setShowDailyTip(false)} 
          user={user}
        />
      )}
    </AuthContext.Provider>
  );
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-teal-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/auth" />;
};

// Admin Route component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-teal-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }
  
  return user && user.is_admin ? children : <Navigate to="/" />;
};

// Admin2 Route component - more permissive check
const Admin2Route = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-teal-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }
  
  // More permissive admin2 check
  const hasAdmin2Access = user && (
    user.is_admin2 === true ||
    user.email === 'admin2@luvcup.app' ||
    (user.is_admin === true && user.email === 'admin2@luvcup.app')
  );
  
  return hasAdmin2Access ? children : <Navigate to="/admin2-login" />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

// Separate component to access AuthContext
function AppContent() {
  const { user } = useAuth();
  const [userResults, setUserResults] = useState([]);

  useEffect(() => {
    const fetchUserResults = async () => {
      if (user) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/quizzes/results`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUserResults(response.data || []);
        } catch (error) {
          console.error('Failed to fetch user results:', error);
        }
      }
    };

    fetchUserResults();
  }, [user]);

  return (
    <>
      <ScrollToTop />
      <ChunkErrorBoundary>
        <Suspense fallback={<LoadingSpinner fullScreen message="Loading page..." />}>
          <div className={user ? 'pb-20 md:pb-0' : ''}>
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/premium" element={<PremiumPage />} />
            <Route path="/games" element={<GamesPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:courseId" element={<CoursesPage />} />
            <Route path="/community" element={<QuestionBoard />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/challenges" element={<ChallengesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/emotions" element={<EmotionsPage />} />
            <Route path="/members-only" element={<MembersOnlyPage />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancel" element={<PaymentCancel />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route 
              path="/quiz/:testId" 
              element={
                <ProtectedRoute>
                  <QuizPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/results" 
              element={
                <ProtectedRoute>
                  <ResultsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/couples" 
              element={
                <ProtectedRoute>
                  <CouplesMode />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/couples/accept/:inviteCode" 
              element={<CouplesInviteAccept />} 
            />
            <Route 
              path="/personalized" 
              element={
                <ProtectedRoute>
                  <PersonalizedTests />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } 
            />
            <Route path="/admin2-login" element={<Admin2Login />} />
            <Route path="/share/:shareId" element={<SharedResult />} />
            <Route path="/invite/:inviteId" element={<InviteLanding />} />
            <Route 
              path="/admin2" 
              element={
                <Admin2Route>
                  <Admin2Dashboard />
                </Admin2Route>
              } 
            />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            
            {/* Commission Program Routes */}
            <Route path="/commission-apply" element={<CommissionApplication />} />
            <Route path="/commission-login" element={<CommissionLogin />} />
            <Route path="/commission-dashboard" element={<CommissionDashboard />} />
            <Route path="/commission-terms" element={<CommissionTerms />} />
            <Route path="/store" element={<StorePage />} />
            <Route path="/store/product/:productId" element={<ProductDetailPage />} />
            
            {/* Messaging Routes */}
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/profile/:userId" element={<UserProfilePage />} />
            
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
        </Suspense>
      </ChunkErrorBoundary>
      <CookieConsent />
      <BottomNav user={user} userResults={userResults} />
    </>
  );
  }

export default App;